import { useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import {
  addQuestionToAssessment,
  deleteQuestionFromAssessment,
  getAssessmentQuestions,
} from '../../../../../../utils/api_service/assessment_api';
import { updateQuestion } from '../../../../../../utils/api_service/job_api';
import {
  convertSecondsToTimeTaken,
  convertStringToUnix,
} from '../../../../../../utils/helpers/time';
import { editQuestionRedux } from '../../../../../../utils/redux_store/features/question_reducer';
import {
  Question,
  QuestionInfoTotalCount,
} from '../../../../../../utils/types/assessment_types';

export const useQuestions = (assessmentId: string) => {
  // — the list you drag/drop on the main page
  const [questionList, setQuestionList] = useState<Question[]>([]);
  // — the “already selected” questions
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);

  // — DnD + expand/collapse + drawer open state
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [isOpenSelectQuestions, setIsOpenSelectQuestions] = useState(false);

  // — header totals
  const [totalCount, setTotalCount] = useState<QuestionInfoTotalCount>({
    totalPoints: 0,
    totalTime: '00:00',
  });

  // 1️⃣ Fetch once, seed both lists
  useEffect(() => {
    getAssessmentQuestions(assessmentId).then((response) => {
      setQuestionList(response);
      setSelectedQuestions(response); // ← seed drawer’s initialSelected

      const totalPoints = response.reduce(
        (sum: number, q: Question) => sum + q.points,
        0,
      );
      const totalSeconds = response.reduce(
        (sum: number, q: Question) => sum + q.time,
        0,
      );
      setTotalCount({
        totalPoints,
        totalTime: convertSecondsToTimeTaken(totalSeconds) || '00:00',
      });
    });
  }, [assessmentId]);

  // 2️⃣ DnD handlers
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const copy = Array.from(questionList);
    const [moved] = copy.splice(result.source.index, 1);
    copy.splice(result.destination.index, 0, moved);
    setQuestionList(copy);
  };

  // 3️⃣ Expand / Collapse
  const toggleExpand = (id: string) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const handleExpandAll = () =>
    setExpandedIds((prev) =>
      prev.length === questionList.length ? [] : questionList.map((q) => q.id),
    );

  // 4️⃣ Drawer open/close
  const handleOpenSelectQuestions = () => {
    setSelectedQuestions(questionList); // re-seed in case questionList changed
    setIsOpenSelectQuestions(true);
  };
  const handleCloseSelectQuestions = () => setIsOpenSelectQuestions(false);

  // 5️⃣ On Save, grab the final array and close
  const handleSaveSelection = (finalSelection: Question[]) => {
    setSelectedQuestions(finalSelection);
    setIsOpenSelectQuestions(false);
    // TODO: persist to backend if needed
  };
  const handleAddQuestion = (q: Question) => {
    addQuestionToAssessment(
      questionList.length.toString(),
      q.id,
      assessmentId,
    ).then((response) => {
      const newQ = { ...q, assessmentQuestionId: response.id };
      setQuestionList((prev) => [...prev, newQ]);

      const prevUnix = convertStringToUnix(totalCount.totalTime) || 0;
      const newUnix = prevUnix + q.time;
      setTotalCount({
        totalPoints: totalCount.totalPoints + q.points,
        totalTime: convertSecondsToTimeTaken(newUnix) || '00:00',
      });
    });
  };
  const dispatch = useDispatch();

  const editQuestion = (question: Question) => {
    updateQuestion(question).then((response) => {
      if (response.id) {
        let questionListCopy = questionList;
        let index = questionListCopy.findIndex(
          (item) => item.id === question.id,
        );
        let editedQuestionPrevState = questionListCopy[index];
        questionListCopy[index] = question;
        setQuestionList(questionListCopy);
        let convertedPrevious = convertStringToUnix(totalCount.totalTime) || 0;
        let timeUnixCalc =
          convertedPrevious - editedQuestionPrevState.time + question.time;
        let convertedTime = convertSecondsToTimeTaken(timeUnixCalc);
        let points: string = question.points.toString();
        setTotalCount({
          totalPoints:
            totalCount.totalPoints -
            editedQuestionPrevState.points +
            parseInt(points),
          totalTime: convertedTime !== undefined ? convertedTime : '00:00',
        });
        dispatch(editQuestionRedux(question));
      }
    });
  };
  const deleteQuestion = (id: string) => {
    deleteQuestionFromAssessment(id).then((response) => {
      if (response.ok) {
        let questionListCopy = questionList;
        let index = questionListCopy.findIndex(
          (item) => item.assessmentQuestionId === id,
        );
        let deletedQuestion = questionListCopy[index];
        questionListCopy.splice(index, 1);
        setQuestionList(questionListCopy);
        let convertedPrevious = convertStringToUnix(totalCount.totalTime) || 0;
        let timeUnixCalc = convertedPrevious - deletedQuestion.time;
        let convertedTime = convertSecondsToTimeTaken(timeUnixCalc);
        setTotalCount({
          totalPoints: totalCount.totalPoints - deletedQuestion.points,
          totalTime: convertedTime !== undefined ? convertedTime : '00:00',
        });
      }
    });
  };
  return {
    expandedIds,
    questionList,
    handleExpandAll,
    handleOpenSelectQuestions,
    handleDragEnd,
    toggleExpand,
    isOpenSelectQuestions,
    handleCloseSelectQuestions,
    handleAddQuestion,
    selectedQuestions,
    handleSaveSelection,
    editQuestion,
    deleteQuestion,
    setQuestionList,
  };
};
